import { defineNuxtRouteMiddleware, navigateTo, useSafeRedirect, useTracking, useUserInfo } from '#imports'

export default defineNuxtRouteMiddleware(async (to, from) => {
    // Preserve auth/step params when opening terms dialogs during signup
    if (import.meta.client && from?.query.auth && !to.query.auth) {
        const isTermsNavigation = to.query.terms || to.query['general-terms'] || to.query['privacy-policy'] || to.query['excluded-games'] || to.query['bingo-games']

        if (isTermsNavigation) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    auth: from.query.auth,
                    step: from.query.step,
                },
            })
        }
    }

    // Skip expensive checks on client-side query-only changes (e.g. opening/closing auth dialog)
    if (import.meta.client && from && to.path === from.path && to.hash === from.hash) {
        return
    }

    const safeRedirect = useSafeRedirect()
    const { isLoggedIn } = await useUserInfo({ server: true, lazy: false })
    const { trackPageRequest } = useTracking()

    if (import.meta.client) {
        const hash = to?.hash
        const includesLoginHash = hash?.includes('login')
        const includesSignupHash = hash?.includes('signup')

        if (includesLoginHash || includesSignupHash) {
            trackPageRequest({ url: window.location.href, referer: document.referrer })

            // Use external to ensure the redirect gets tracked
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    auth: includesLoginHash ? 'login' : 'signup',
                },
                hash: undefined,
            }, { external: true })
        }

        const bonusCode = hash.split('bonusCode=')?.[1]

        if (bonusCode) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    bonusCode,
                },
                hash: undefined,
            })
        }

        const bundleId = hash.split('bundleId=')?.[1]

        if (bundleId) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    bundleId,
                },
                hash: undefined,
            })
        }

        const promotionId = hash.split('promotionId=')?.[1]

        if (promotionId) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    promotionId,
                },
                hash: undefined,
            })
        }

        const promotionReferenceId = hash.split('promotionReferenceId=')?.[1]

        if (promotionReferenceId) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    promotionReferenceId,
                },
                hash: undefined,
            })
        }

        const referenceId = hash.split('referenceId=')?.[1]

        if (referenceId) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    referenceId,
                },
                hash: undefined,
            })
        }

        const bonusReferenceId = hash.split('bonusReferenceId=')?.[1]

        if (bonusReferenceId) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                    bonusReferenceId,
                },
                hash: undefined,
            })
        }

        const includesTermsHash = hash?.includes('terms')

        if (includesTermsHash) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    terms: 'true',
                },
                hash: undefined,
            })
        }

        const includesExcludedGamesHash = hash?.includes('excluded-games')

        if (includesExcludedGamesHash) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    'excluded-games': 'true',
                },
                hash: undefined,
            })
        }

        const includesBingoGamesHash = hash?.includes('bingo-games')
        if (includesBingoGamesHash) {
            return navigateTo({
                ...to,
                query: {
                    ...to.query,
                    'bingo-games': 'true',
                },
                hash: undefined,
            })
        }
    }

    if (!isLoggedIn.value) {
        // Check signup has 1 as the initial step
        if (to.query.auth === 'signup') {
            const step = Number(to.query.step)

            if (step !== 1) {
                if (import.meta.server) {
                    // User must start at step 1 when navigating directly into signup
                    return navigateTo({
                        ...to,
                        query: {
                            ...to.query,
                            step: '1',
                        },
                    })
                }
            }
        }

        return
    }

    const returnUrl = to.query.returnUrl?.toString()

    if (returnUrl) {
        if (safeRedirect.isSafe(returnUrl)) {
            // Go to returnUrl
            return navigateTo({
                ...to,
                path: returnUrl,
                query: {
                    ...to.query,
                    returnUrl: undefined,
                },
            }, {
                external: true,
                redirectCode: 302,
            })
        }

        // Continue routing but remove returnUrl param
        return navigateTo({
            ...to,
            query: {
                ...to.query,
                returnUrl: undefined,
            },
        }, {
            external: true,
        })
    }

    const authType = to.query.auth as string | undefined
    const cleanupAuthTypes = ['signup', 'login', 'reactivate', 'magic-link']

    if (authType && cleanupAuthTypes.includes(authType)) {
        if (authType === 'login' && to.query.mitid === 'true') return

        return navigateTo({
            ...to,
            query: {
                ...to.query,
                auth: undefined,
                step: undefined,
            },
        })
    }
})
